import { Plus, Trash2 } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import type { PersonalizationType } from '@aeris/contracts'
import {
  Button,
  CheckboxField,
  Input,
  InlineAlert,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
  VisuallyHidden,
} from '@aeris/ui'
import {
  UNIFORM_PIECE_SUGGESTIONS,
  UNIFORM_SIZES,
  parsePersonalizationPaste,
  type UniformSize,
} from '@aeris/uniforms'

import {
  gradeTotal,
  nextKey,
  personalizationTotal,
  quantityOf,
  rowTotal,
  type GradeRowDraft,
  type PersonalizationDraft,
} from '../lib/uniform-grade-draft.ts'

/**
 * A grade de tamanhos e os personalizados — F170.
 *
 * A forma é a do Excel da loja: uma linha por peça, uma coluna por tamanho, o
 * total na ponta. O banco guarda uma linha por tamanho (é o que deixa somar e
 * filtrar); aqui a tela remonta a planilha e desmonta de volta ao salvar.
 *
 * Os tamanhos vêm em grupos que se ligam: o adulto está sempre à vista, e o
 * especial, os grandes (G1–G4) e o infantil aparecem quando alguém pede — ou
 * sozinhos, quando a grade gravada já usa algum deles.
 */

const SIZE_GROUPS = [
  {
    key: 'adulto',
    label: 'Adulto',
    sizes: ['PP', 'P', 'M', 'G', 'GG', 'XG', 'EXG'],
  },
  { key: 'especial', label: 'Especial (ESP)', sizes: ['ESP'] },
  { key: 'grandes', label: 'G1 a G4', sizes: ['G1', 'G2', 'G3', 'G4'] },
  {
    key: 'infantil',
    label: 'Infantil',
    sizes: ['2', '4', '6', '8', '10', '12', '14', '16'],
  },
] as const satisfies readonly {
  key: string
  label: string
  sizes: readonly UniformSize[]
}[]

type GroupKey = (typeof SIZE_GROUPS)[number]['key']

/** Os modelos da planilha da loja — sugestão, não lista fechada. */
const MODEL_SUGGESTIONS = [
  'Camisa Masc',
  'Camisa Fem',
  'Camisa e Shorts Masc',
  'Camisa e Shorts Fem',
  'Camisa e Regata Masc',
  'Regata Masc',
  'Regata Fem',
  'Manga Longa Masc',
  'Manga Longa Fem',
  'Shorts Masculino',
  'Shorts Fem',
  'Shorts com Bolso masc',
  'Manguito',
  'Sunga',
  'Top',
]

export const GradeEditor = ({
  rows,
  onChange,
  disabled,
}: {
  rows: readonly GradeRowDraft[]
  onChange: (rows: GradeRowDraft[]) => void
  disabled: boolean
}): ReactNode => {
  const listId = useId()
  const initiallyUsed = new Set<GroupKey>(
    SIZE_GROUPS.filter((group) =>
      rows.some((row) =>
        group.sizes.some((size) => quantityOf(row.quantities[size]) > 0)
      )
    ).map((group) => group.key)
  )
  const [shown, setShown] = useState<ReadonlySet<GroupKey>>(
    new Set<GroupKey>(['adulto', ...initiallyUsed])
  )
  const sizes: UniformSize[] = SIZE_GROUPS.filter((group) =>
    shown.has(group.key)
  ).flatMap((group) => [...group.sizes])

  const update = (key: string, patch: Partial<GradeRowDraft>): void => {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))
  }

  const lastBlock = rows.at(-1)?.block ?? 'Camisa Masc'

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-4">
        {SIZE_GROUPS.filter((group) => group.key !== 'adulto').map((group) => (
          <CheckboxField
            key={group.key}
            label={group.label}
            checked={shown.has(group.key)}
            onCheckedChange={(checked) => {
              setShown((current) => {
                const next = new Set(current)
                if (checked === true) next.add(group.key)
                else next.delete(group.key)
                return next
              })
            }}
          />
        ))}
      </div>

      <datalist id={`${listId}-modelos`}>
        {MODEL_SUGGESTIONS.map((model) => (
          <option key={model} value={model} />
        ))}
      </datalist>
      <datalist id={`${listId}-pecas`}>
        {UNIFORM_PIECE_SUGGESTIONS.map((piece) => (
          <option key={piece} value={piece} />
        ))}
      </datalist>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Modelo</TableHeaderCell>
              <TableHeaderCell>Peça</TableHeaderCell>
              {sizes.map((size) => (
                <TableHeaderCell key={size} className="w-14 text-center">
                  {size}
                </TableHeaderCell>
              ))}
              <TableHeaderCell numeric>Total</TableHeaderCell>
              <TableHeaderCell>
                <VisuallyHidden>Tirar</VisuallyHidden>
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.key}>
                <TableCell>
                  <Input
                    aria-label={`Modelo da linha ${String(index + 1)}`}
                    list={`${listId}-modelos`}
                    value={row.block}
                    maxLength={60}
                    disabled={disabled}
                    className="min-w-40"
                    onChange={(event) => {
                      update(row.key, { block: event.target.value })
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    aria-label={`Peça da linha ${String(index + 1)}`}
                    list={`${listId}-pecas`}
                    value={row.piece}
                    maxLength={40}
                    disabled={disabled}
                    className="min-w-32"
                    onChange={(event) => {
                      update(row.key, { piece: event.target.value })
                    }}
                  />
                </TableCell>
                {sizes.map((size) => (
                  <TableCell key={size} className="px-1">
                    <Input
                      aria-label={`${row.piece === '' ? 'Peça' : row.piece} tamanho ${size}`}
                      inputMode="numeric"
                      value={row.quantities[size] ?? ''}
                      disabled={disabled}
                      className="w-14 text-center"
                      onChange={(event) => {
                        const digits = event.target.value.replace(/\D/g, '')
                        update(row.key, {
                          quantities: { ...row.quantities, [size]: digits },
                        })
                      }}
                    />
                  </TableCell>
                ))}
                <TableCell numeric className="font-(--weight-medium)">
                  {rowTotal(row)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    aria-label={`Tirar a linha ${String(index + 1)}`}
                    onClick={() => {
                      onChange(rows.filter((other) => other.key !== row.key))
                    }}
                  >
                    <Trash2 aria-hidden="true" className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={disabled}
          leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
          onClick={() => {
            onChange([
              ...rows,
              { key: nextKey(), block: lastBlock, piece: '', quantities: {} },
            ])
          }}
        >
          Linha
        </Button>
        <p className="text-(length:--text-sm) text-(--text-secondary)">
          Na grade: <strong>{gradeTotal(rows)}</strong> peças
        </p>
      </div>
    </div>
  )
}

/**
 * A lista dos personalizados: a peça com o nome e o número do atleta.
 *
 * Chega pelo WhatsApp ou numa planilha, com dezenas de nomes. Por isso existe o
 * "Colar": as colunas na ordem da grade da loja — Nome, Peça, Número, Qtde,
 * Tamanho —, e as linhas que não fecham voltam numeradas em vez de sumirem.
 */
export const PersonalizationEditor = ({
  rows,
  onChange,
  disabled,
}: {
  rows: readonly PersonalizationDraft[]
  onChange: (rows: PersonalizationDraft[]) => void
  disabled: boolean
}): ReactNode => {
  const [pasting, setPasting] = useState(false)
  const [pasted, setPasted] = useState('')
  const [rejected, setRejected] = useState<readonly number[]>([])

  const update = (key: string, patch: Partial<PersonalizationType>): void => {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))
  }

  return (
    <div className="flex flex-col gap-3">
      {rows.length === 0 ? (
        <p className="text-(length:--text-sm) text-(--text-muted)">
          Nenhum personalizado. Adicione um por um ou cole a lista da planilha.
        </p>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Nome</TableHeaderCell>
                <TableHeaderCell>Peça</TableHeaderCell>
                <TableHeaderCell>Cor</TableHeaderCell>
                <TableHeaderCell className="w-20">Nº</TableHeaderCell>
                <TableHeaderCell className="w-20">Qtde</TableHeaderCell>
                <TableHeaderCell className="w-24">Tam.</TableHeaderCell>
                <TableHeaderCell>
                  <VisuallyHidden>Tirar</VisuallyHidden>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={row.key}>
                  <TableCell>
                    <Input
                      aria-label={`Nome ${String(index + 1)}`}
                      value={row.name}
                      maxLength={80}
                      disabled={disabled}
                      onChange={(event) => {
                        update(row.key, { name: event.target.value })
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Peça ${String(index + 1)}`}
                      value={row.piece}
                      maxLength={40}
                      disabled={disabled}
                      onChange={(event) => {
                        update(row.key, { piece: event.target.value })
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Cor ${String(index + 1)}`}
                      value={row.color}
                      maxLength={60}
                      disabled={disabled}
                      placeholder="Opcional"
                      onChange={(event) => {
                        update(row.key, { color: event.target.value })
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Número ${String(index + 1)}`}
                      value={row.number}
                      maxLength={10}
                      disabled={disabled}
                      onChange={(event) => {
                        update(row.key, { number: event.target.value })
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Quantidade ${String(index + 1)}`}
                      inputMode="numeric"
                      value={String(row.quantity)}
                      disabled={disabled}
                      onChange={(event) => {
                        const value = Number(
                          event.target.value.replace(/\D/g, '')
                        )
                        update(row.key, { quantity: value > 0 ? value : 1 })
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      aria-label={`Tamanho ${String(index + 1)}`}
                      value={row.size}
                      disabled={disabled}
                      onChange={(event) => {
                        update(row.key, {
                          size: event.target.value as UniformSize,
                        })
                      }}
                    >
                      {UNIFORM_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      aria-label={`Tirar ${row.name === '' ? 'a linha' : row.name}`}
                      onClick={() => {
                        onChange(rows.filter((other) => other.key !== row.key))
                      }}
                    >
                      <Trash2 aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled}
            leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              onChange([
                ...rows,
                {
                  key: nextKey(),
                  name: '',
                  piece: rows.at(-1)?.piece ?? 'Camisa',
                  color: '',
                  number: '',
                  quantity: 1,
                  size: 'M',
                },
              ])
            }}
          >
            Personalizado
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => {
              setPasting((open) => !open)
            }}
          >
            Colar da planilha
          </Button>
        </div>
        <p className="text-(length:--text-sm) text-(--text-secondary)">
          Personalizados: <strong>{personalizationTotal(rows)}</strong> peças
        </p>
      </div>

      {pasting ? (
        <div className="flex flex-col gap-2">
          <Textarea
            aria-label="Lista colada"
            rows={6}
            value={pasted}
            placeholder={
              'Nome\tPeça\tNº\tQtde\tTamanho\nJoão\tCamisa\t10\t1\tG'
            }
            onChange={(event) => {
              setPasted(event.target.value)
            }}
          />
          <p className="text-(length:--text-xs) text-(--text-muted)">
            Colunas na ordem da grade: Nome, Peça, Número, Qtde e Tamanho —
            separadas por tabulação (copiado do Excel) ou por ponto e vírgula.
          </p>
          <div>
            <Button
              variant="primary"
              size="sm"
              disabled={pasted.trim() === ''}
              onClick={() => {
                const result = parsePersonalizationPaste(pasted)
                onChange([
                  ...rows,
                  ...result.rows.map((row) => ({
                    ...row,
                    color: '',
                    key: nextKey(),
                  })),
                ])
                setRejected(result.rejected)
                setPasted('')
                if (result.rejected.length === 0) setPasting(false)
              }}
            >
              Acrescentar à lista
            </Button>
          </div>
        </div>
      ) : null}

      {rejected.length === 0 ? null : (
        <InlineAlert tone="warning">
          {rejected.length === 1
            ? `A linha ${String(rejected[0])} não foi lida`
            : `As linhas ${rejected.join(', ')} não foram lidas`}{' '}
          — falta o nome, o tamanho não é da grade, ou a quantidade não é um
          número. As outras entraram na lista.
        </InlineAlert>
      )}
    </div>
  )
}
